import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, FileText, Settings, AlertCircle, ArrowLeft } from 'lucide-react';
import SubscriptionManagement from '../components/Billing/SubscriptionManagement';
import CardManagement from '../components/Billing/CardManagement';
import InvoiceView from '../components/Billing/InvoiceView';
import { useAuth } from '../contexts/AuthContext';

const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'subscription' | 'cards' | 'invoices'>('subscription');
  const [userSiteId, setUserSiteId] = useState<string>('');

  // Get the user's site from context or API
  React.useEffect(() => {
    const fetchUserSite = async () => {
      try {
        console.log('Fetching user site for user:', user);
        
        // Get the active site from localStorage
        const activeSiteId = localStorage.getItem('activeSiteId');
        if (activeSiteId) {
          console.log('Found active site ID:', activeSiteId);
          setUserSiteId(activeSiteId);
          return;
        }

        // Fetch sites the user has access to
        const response = await fetch('/api/sites', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch sites: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Sites API response:', data);
        
        // Check if user has access to any sites
        if (data.sites && data.sites.length > 0) {
          // Use the first site the user has access to
          const firstSite = data.sites[0];
          console.log('Using first site:', firstSite);
          setUserSiteId(firstSite._id);
          localStorage.setItem('activeSiteId', firstSite._id);
        } else {
          // User has no site access
          console.error('User has no site access');
          setUserSiteId(''); // Clear the site ID to show proper error
        }
      } catch (error) {
        console.error('Failed to fetch user site:', error);
        setUserSiteId(''); // Clear the site ID to show proper error
      }
    };

    if (user) {
      fetchUserSite();
    }
  }, [user]);

  const tabs = [
    {
      id: 'subscription',
      label: 'Subscription',
      icon: CreditCard,
      description: 'Manage your subscription plan and billing settings'
    },
    {
      id: 'cards',
      label: 'Payment Methods',
      icon: CreditCard,
      description: 'Manage your payment cards and billing methods'
    },
    {
      id: 'invoices',
      label: 'Invoices',
      icon: FileText,
      description: 'View and download your billing invoices'
    }
  ];

  if (!userSiteId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">No Site Access</h2>
          <p className="text-gray-600 mb-4">
            You don't have access to any site yet. Please contact your administrator to get assigned to a site.
          </p>
          <div className="text-sm text-gray-500">
            <p>If you believe this is an error, please check:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>You are properly logged in</li>
              <li>Your account has been assigned to a site</li>
              <li>Your account has the correct permissions</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin"
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Admin
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Billing Center</h1>
              <p className="mt-2 text-gray-600">
                Manage your subscription, payment methods, and view billing history
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as 'subscription' | 'cards' | 'invoices')}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon
                    className={`mr-2 h-5 w-5 ${
                      activeTab === tab.id
                        ? 'text-primary-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'subscription' && (
            <div>
              <SubscriptionManagement 
                siteId={userSiteId} 
                isSuperAdmin={user?.role === 'super_admin'}
              />
            </div>
          )}

          {activeTab === 'cards' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
                <p className="text-gray-600">
                  Manage your payment cards and billing methods for subscription payments
                </p>
              </div>
              <CardManagement siteId={userSiteId} />
            </div>
          )}

          {activeTab === 'invoices' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Billing History</h2>
                <p className="text-gray-600">
                  View and download all your invoices and billing statements
                </p>
              </div>
              <InvoiceView siteId={userSiteId} />
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Settings className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-blue-900">Need Help?</h3>
              <div className="mt-2 text-blue-700">
                <p className="mb-2">
                  If you have questions about your subscription or need help with billing, we're here to help:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Email us at billing@laundrette-system.com</li>
                  <li>Call our support team at +44 20 7123 4567</li>
                  <li>View our documentation at docs.laundrette-system.com</li>
                  <li>Chat with our support team (available 9am-5pm GMT)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
